import type { ReleaseBulkAction } from '@planuze/admin-contracts';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos COMPONENTES de releases (ADR-0659/0670/0697/0809): painel/tabela do bucket R2, barra
   de ação em lote, dialog de confirmação, modal de notas e a tabela principal. O header/toolbar/
   toasts da PÁGINA ficam em `content/sections/releases.ts` (frente de routes/) — este arquivo é só
   o que os componentes de `components/releases/` desenham por conta própria. O mapeamento de erro
   (`releaseErrorMessage`) NÃO está aqui: vive em `releases-error.ts` com um parâmetro `locale?`
   opcional, pelo mesmo motivo de `pack-catalog-labels.ts` (consumido só por `routes/`, que ainda
   chama com um único argumento). */
export interface ReleasesAdminContent {
  readonly bucketPanel: {
    readonly loadError: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly auditNotePrefix: string;
    readonly auditNoteStrong: string;
  };
  readonly bulkBar: {
    readonly actionLabels: Readonly<Record<ReleaseBulkAction, string>>;
    readonly selectedCount: (count: number) => string;
    readonly actionAriaLabel: string;
    readonly apply: string;
    readonly clearSelection: string;
    readonly promotionStorageUnavailable: string;
  };
  readonly confirmDialog: {
    readonly bulkTitles: Readonly<Record<ReleaseBulkAction, string>>;
    readonly bulkEffects: Readonly<Record<ReleaseBulkAction, string>>;
    readonly deleteTitle: string;
    readonly deleteBody: (version: string, platform: string, arch: string, kind: string) => string;
    readonly purgeTitle: string;
    readonly purgeBody: (channel: string, version: string) => string;
    readonly purgeConfirmationLabel: (target: string) => string;
    readonly purgeReasonLabel: string;
    readonly purgeReasonPlaceholder: string;
    readonly purgeWarning: string;
    readonly bulkBody: (count: number, effect: string) => string;
    readonly irreversibleWarningPrefix: string;
    readonly irreversibleWarningStrong: string;
    readonly irreversibleWarningSuffix: string;
    readonly cancel: string;
    readonly confirm: string;
    readonly confirmPurge: string;
  };
  readonly notesModal: {
    readonly savedToast: string;
    readonly saveErrorToast: string;
    readonly title: (version: string) => string;
    readonly description: string;
    readonly notesLabel: string;
    readonly notesPlaceholder: string;
    readonly cancel: string;
    readonly save: string;
  };
  readonly table: {
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly clearFilters: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly selectPageAriaLabel: string;
    readonly selectRowAriaLabel: (version: string, platformArch: string) => string;
    readonly columnVersion: string;
    readonly columnChannel: string;
    readonly columnPlatform: string;
    readonly columnStatus: string;
    readonly columnMandatory: string;
    readonly columnDownloads: string;
    readonly columnActions: string;
    readonly installerSuffix: string;
    readonly mandatoryBadge: string;
    readonly lastDownloadTitle: (date: string) => string;
    readonly promote: string;
    readonly promoteMandatory: string;
    readonly markMandatory: string;
    readonly markOptional: string;
    readonly takedown: string;
    readonly editNotes: string;
    readonly delete: string;
    readonly purge: string;
    readonly continuePurge: string;
    readonly storageDeletingBadge: string;
    readonly storageDeletedBadge: string;
  };
}

const RELEASES_ADMIN_CONTENT: Bilingual<ReleasesAdminContent> = {
  'pt-BR': {
    bucketPanel: {
      loadError: 'Falha ao carregar os objetos do bucket.',
      emptyTitle: 'Bucket vazio',
      emptyDescription: 'Nenhum objeto no R2 ainda. Os binários aparecem aqui após o CI publicar.',
      auditNotePrefix:
        'Visão bruta do que já subiu no R2 (auditoria). O registro é feito por versão em',
      auditNoteStrong: 'Registrar release',
    },
    bulkBar: {
      actionLabels: {
        promote: 'Promover',
        promoteMandatory: 'Promover como obrigatória',
        takedown: 'Tirar do ar',
        delete: 'Remover do índice',
      },
      selectedCount: (count) => `${count} versão(ões) selecionada(s)`,
      actionAriaLabel: 'Ação em lote',
      apply: 'Aplicar',
      clearSelection: 'Limpar seleção',
      promotionStorageUnavailable:
        'A seleção contém uma versão com o R2 excluído ou em exclusão. Escolha outra ação.',
    },
    confirmDialog: {
      bulkTitles: {
        promote: 'Promover em lote',
        promoteMandatory: 'Promover como obrigatória em lote',
        takedown: 'Tirar do ar em lote',
        delete: 'Remover registros em lote',
      },
      bulkEffects: {
        promote: 'ficam disponíveis para todos os usuários do canal.',
        promoteMandatory:
          'ficam disponíveis e definem a versão mínima do canal. Quem usa versões anteriores precisará atualizar.',
        takedown: 'saem do feed de atualização e do download da landing page.',
        delete:
          'têm o registro e as métricas removidos do índice; os binários publicados permanecem imutáveis.',
      },
      deleteTitle: 'Remover registro desta versão',
      deleteBody: (version, platform, arch, kind) =>
        `${version} · ${platform}/${arch} · ${kind} — registro e métricas saem do índice; o binário permanece no R2/GitHub.`,
      purgeTitle: 'Excluir versão permanentemente do R2',
      purgeBody: (channel, version) =>
        `${channel}/${version} — todos os artefatos e sidecars deste prefixo serão apagados do R2. O histórico, os registros, as métricas e a GitHub Release permanecerão intactos.`,
      purgeConfirmationLabel: (target) =>
        `Digite o identificador completo ${target} para confirmar`,
      purgeReasonLabel: 'Motivo da exclusão',
      purgeReasonPlaceholder: 'Explique por que os objetos desta versão devem ser removidos do R2',
      purgeWarning:
        'Esta operação apaga o prefixo inteiro do R2 e não pode ser desfeita. Se algum artefato ainda estiver no ar, tire-o do ar antes.',
      bulkBody: (count, effect) => `${count} versão(ões) selecionada(s) ${effect}`,
      irreversibleWarningPrefix: 'O',
      irreversibleWarningStrong: 'binário permanece imutável',
      irreversibleWarningSuffix:
        'no R2/GitHub e pode ser redescoberto. A linha e suas métricas serão removidas. Versões no ar são recusadas: tire do ar antes.',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      confirmPurge: 'Excluir permanentemente',
    },
    notesModal: {
      savedToast: 'Notas salvas.',
      saveErrorToast: 'Falha ao salvar as notas.',
      title: (version) => `Notas da versão ${version}`,
      description:
        'A alteração vale para todos os artefatos desta versão e aparece no app e no histórico público.',
      notesLabel: 'Notas (markdown) — exibidas no app na tela "Versões"',
      notesPlaceholder: '## Novidades\n- ...\n\n## Correções\n- ...',
      cancel: 'Cancelar',
      save: 'Salvar',
    },
    table: {
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhuma release corresponde aos filtros.',
      clearFilters: 'Limpar filtros',
      emptyTitle: 'Nenhuma release registrada',
      emptyDescription: 'Registre a primeira versão a partir do que o CI publicou no R2.',
      selectPageAriaLabel: 'Selecionar as versões desta página',
      selectRowAriaLabel: (version, platformArch) => `Selecionar ${version} ${platformArch}`,
      columnVersion: 'Versão',
      columnChannel: 'Canal',
      columnPlatform: 'Plataforma',
      columnStatus: 'Status',
      columnMandatory: 'Obrigatória',
      columnDownloads: 'Downloads',
      columnActions: 'Ações',
      installerSuffix: 'instalador',
      mandatoryBadge: 'obrigatória',
      lastDownloadTitle: (date) => `Último em ${date}`,
      promote: 'Promover',
      promoteMandatory: 'Promover obrigatória',
      markMandatory: 'Marcar obrigatória',
      markOptional: 'Marcar opcional',
      takedown: 'Tirar do ar',
      editNotes: 'Editar notas',
      delete: 'Remover registro',
      purge: 'Excluir versão do R2',
      continuePurge: 'Continuar exclusão do R2',
      storageDeletingBadge: 'Exclusão do R2 pendente',
      storageDeletedBadge: 'R2 excluído',
    },
  },
  'en-US': {
    bucketPanel: {
      loadError: 'Failed to load the bucket objects.',
      emptyTitle: 'Empty bucket',
      emptyDescription: 'No objects in R2 yet. Binaries show up here once CI publishes them.',
      auditNotePrefix:
        'Raw view of what has already been uploaded to R2 (audit). Registration happens per version in',
      auditNoteStrong: 'Register release',
    },
    bulkBar: {
      actionLabels: {
        promote: 'Promote',
        promoteMandatory: 'Promote as mandatory',
        takedown: 'Take down',
        delete: 'Remove from index',
      },
      selectedCount: (count) => `${count} version(s) selected`,
      actionAriaLabel: 'Bulk action',
      apply: 'Apply',
      clearSelection: 'Clear selection',
      promotionStorageUnavailable:
        'The selection includes a version whose R2 storage is deleted or being deleted. Choose another action.',
    },
    confirmDialog: {
      bulkTitles: {
        promote: 'Bulk promote',
        promoteMandatory: 'Bulk promote as mandatory',
        takedown: 'Bulk take down',
        delete: 'Bulk remove records',
      },
      bulkEffects: {
        promote: 'become available to every user on the channel.',
        promoteMandatory:
          'become available and set the channel’s minimum version. People on older versions will need to update.',
        takedown: 'leave the update feed and the landing page download.',
        delete:
          'have their record and metrics removed from the index; published binaries remain immutable.',
      },
      deleteTitle: 'Remove this version record',
      deleteBody: (version, platform, arch, kind) =>
        `${version} · ${platform}/${arch} · ${kind} — record and metrics leave the index; the binary remains in R2/GitHub.`,
      purgeTitle: 'Permanently delete version from R2',
      purgeBody: (channel, version) =>
        `${channel}/${version} — every artifact and sidecar under this prefix will be deleted from R2. History, records, metrics, and the GitHub Release will remain intact.`,
      purgeConfirmationLabel: (target) => `Type the full identifier ${target} to confirm`,
      purgeReasonLabel: 'Deletion reason',
      purgeReasonPlaceholder: 'Explain why this version’s objects must be removed from R2',
      purgeWarning:
        'This operation deletes the entire R2 prefix and cannot be undone. If any artifact is still live, take it down first.',
      bulkBody: (count, effect) => `${count} version(s) selected ${effect}`,
      irreversibleWarningPrefix: 'The',
      irreversibleWarningStrong: 'binary remains immutable',
      irreversibleWarningSuffix:
        'in R2/GitHub and can be rediscovered. The row and its metrics will be removed. Live versions are refused: take them down first.',
      cancel: 'Cancel',
      confirm: 'Confirm',
      confirmPurge: 'Delete permanently',
    },
    notesModal: {
      savedToast: 'Notes saved.',
      saveErrorToast: 'Failed to save the notes.',
      title: (version) => `Notes for version ${version}`,
      description:
        'The change applies to every artifact for this version and appears in the app and public history.',
      notesLabel: 'Notes (markdown) — shown in the app on the "Versions" screen',
      notesPlaceholder: '## New\n- ...\n\n## Fixes\n- ...',
      cancel: 'Cancel',
      save: 'Save',
    },
    table: {
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No release matches the filters.',
      clearFilters: 'Clear filters',
      emptyTitle: 'No release registered',
      emptyDescription: 'Register the first version from what CI published to R2.',
      selectPageAriaLabel: 'Select the versions on this page',
      selectRowAriaLabel: (version, platformArch) => `Select ${version} ${platformArch}`,
      columnVersion: 'Version',
      columnChannel: 'Channel',
      columnPlatform: 'Platform',
      columnStatus: 'Status',
      columnMandatory: 'Mandatory',
      columnDownloads: 'Downloads',
      columnActions: 'Actions',
      installerSuffix: 'installer',
      mandatoryBadge: 'mandatory',
      lastDownloadTitle: (date) => `Last on ${date}`,
      promote: 'Promote',
      promoteMandatory: 'Promote mandatory',
      markMandatory: 'Mark mandatory',
      markOptional: 'Mark optional',
      takedown: 'Take down',
      editNotes: 'Edit notes',
      delete: 'Remove record',
      purge: 'Delete version from R2',
      continuePurge: 'Continue R2 deletion',
      storageDeletingBadge: 'R2 deletion pending',
      storageDeletedBadge: 'R2 deleted',
    },
  },
};

export const useReleasesAdminContent = (): ReleasesAdminContent =>
  useBilingual(RELEASES_ADMIN_CONTENT);
